import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import type { LoginResponse } from '@/api/types/auth';
import { isInComposeMode } from '@/config/office/office';
import { queryKeys } from '@/config/query/queryKeys';
import { useDebounce } from '@/utils/hooks/useDebounce';
import {
  type FilterMode,
  determineInitialFilterMode,
  useOfficeContextStore,
} from '@/utils/stores/useOfficeContextStore';

import { useEmailData } from './useEmailData';

const setStateReducer = <T>(prev: T, action: React.SetStateAction<T>): T =>
  typeof action === 'function' ? (action as (prevState: T) => T)(prev) : action;

interface UseEmailContextDialogStateParams {
  activeSessionContext?:
    | {
        context?: string;
        conversationId?: string;
        emailId?: string;
      }
    | undefined;
  contextEmailIds?: Array<string> | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  open: boolean;
  sessionId?: string | null | undefined;
}

export function useEmailContextDialogState({
  activeSessionContext,
  contextEmailIds,
  onOpenChange,
  open,
  sessionId,
}: UseEmailContextDialogStateParams) {
  const getDefaultFilterMode = useOfficeContextStore((s) => s.getDefaultFilterMode);
  const getCurrentFilterMode = useOfficeContextStore((s) => s.getCurrentFilterMode);
  const contextInfo = useOfficeContextStore((s) => s.contextInfo);
  const storeSessionContext = useOfficeContextStore((s) => s.activeSessionContext);
  const sessionContext = storeSessionContext ?? activeSessionContext;
  const setSelectedFilterMode = useOfficeContextStore((s) => s.setSelectedFilterMode);
  const selectedFilterMode = useOfficeContextStore((s) => s.selectedFilterMode);
  const hasActiveEmail = useOfficeContextStore((s) => s.hasActiveEmail);
  const hasActiveThread = useOfficeContextStore((s) => s.hasActiveThread);
  const selectedCustomEmails = useOfficeContextStore((s) => s.selectedCustomEmails);
  const setSelectedCustomEmails = useOfficeContextStore((s) => s.setSelectedCustomEmails);
  const selectedFolderId = useOfficeContextStore((s) => s.selectedFolderId);
  const setSelectedFolder = useOfficeContextStore((s) => s.setSelectedFolder);
  const { data: loginUserData } = useQuery<LoginResponse['user']>({
    enabled: false,
    queryKey: queryKeys.auth.user(),
  });
  const ragSearchEnabled = loginUserData?.ragSearchEnabled ?? false;
  const showSyncingState = ragSearchEnabled && !loginUserData?.initialSyncCompletedAt;

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const composeMode = isInComposeMode();
  const canUseCurrentEmail = hasActiveEmail();
  const canUseCurrentThread = hasActiveThread();

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setSearchQuery('');
      }
      onOpenChange?.(newOpen);
    },
    [onOpenChange],
  );

  const initialFilterMode = useMemo(() => getDefaultFilterMode(), [getDefaultFilterMode]);

  const [filterMode, setFilterMode] = useReducer(
    setStateReducer<FilterMode>,
    determineInitialFilterMode({
      activeSessionContext: sessionContext,
      contextInfo,
      currentMode: getCurrentFilterMode(),
      selectedCustomEmails,
    }),
  );

  const userManuallyChangedModeRef = useRef(false);
  const prevOpenRef = useRef(open);
  const prevSessionIdRef = useRef<string | null | undefined>(sessionId);

  useEffect(() => {
    if (prevSessionIdRef.current !== sessionId) {
      prevSessionIdRef.current = sessionId;
      userManuallyChangedModeRef.current = false;
      setFilterMode(() =>
        determineInitialFilterMode({
          activeSessionContext: sessionContext,
          contextInfo,
          currentMode: getDefaultFilterMode(),
          selectedCustomEmails: [],
        }),
      );
    }
  }, [sessionId, sessionContext, contextInfo, getDefaultFilterMode]);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      userManuallyChangedModeRef.current = false;
      const storeMode = selectedFilterMode ?? getCurrentFilterMode();
      setFilterMode(() => storeMode);
    }

    prevOpenRef.current = open;
  }, [open, getCurrentFilterMode, selectedFilterMode]);

  useEffect(() => {
    if (userManuallyChangedModeRef.current) {
      return;
    }

    if (selectedFilterMode !== null) {
      return;
    }

    if (!open) {
      return;
    }

    setFilterMode((prev) => {
      if (
        selectedCustomEmails.length === 0 &&
        prev === 'custom' &&
        sessionContext?.context !== 'CUSTOM'
      ) {
        return getDefaultFilterMode();
      }

      if (prev === 'custom' && selectedCustomEmails.length > 0) {
        return prev;
      }

      return determineInitialFilterMode({
        activeSessionContext: sessionContext,
        contextInfo,
        currentMode: prev,
        selectedCustomEmails,
      });
    });
  }, [
    sessionContext,
    contextInfo,
    selectedCustomEmails,
    getDefaultFilterMode,
    open,
    selectedFilterMode,
  ]);

  const [selectedEmails, setSelectedEmails] = useReducer(
    setStateReducer<Set<string>>,
    new Set(filterMode === 'custom' ? selectedCustomEmails : []),
  );

  useEffect(() => {
    if (selectedCustomEmails.length === 0) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(selectedCustomEmails));
    }
  }, [selectedCustomEmails]);

  const emailData = useEmailData({
    contextEmailIds,
    debouncedSearch,
    open,
    selectedEmails,
    sessionId: sessionId ?? null,
  });

  const [counterHighlight, setCounterHighlight] = useReducer(setStateReducer<boolean>, false);
  const selectedEmailsSectionRef = useRef<HTMLDivElement>(null);
  const prevSelectedCountRef = useRef(selectedEmails.size);
  const counterHighlightTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const showCheckboxes = filterMode === 'custom';

  useEffect(() => {
    if (selectedEmails.size > prevSelectedCountRef.current) {
      setCounterHighlight(true);
      if (counterHighlightTimerRef.current !== undefined) {
        clearTimeout(counterHighlightTimerRef.current);
      }
      counterHighlightTimerRef.current = setTimeout(() => setCounterHighlight(false), 600);
    }
    prevSelectedCountRef.current = selectedEmails.size;
  }, [selectedEmails.size]);

  useEffect(() => {
    return () => {
      if (counterHighlightTimerRef.current !== undefined) {
        clearTimeout(counterHighlightTimerRef.current);
      }
    };
  }, []);

  const selectedCount = useMemo(() => {
    return filterMode === 'custom' ? selectedEmails.size : 0;
  }, [filterMode, selectedEmails]);

  const resultCount = useMemo(() => {
    return emailData.emails.length;
  }, [emailData.emails.length]);

  const handleFilterModeChange = (mode: FilterMode) => {
    setSearchQuery('');
    userManuallyChangedModeRef.current = true;

    if (filterMode !== 'custom' && mode === 'custom') {
      setFilterMode('custom');
      setSelectedFilterMode('custom');
      if (contextEmailIds?.length) {
        setSelectedCustomEmails((prev) => {
          return prev.length > 0 ? prev : contextEmailIds;
        });
      }
      return;
    }

    if (filterMode === 'custom' && mode !== 'custom') {
      setSelectedEmails(new Set());
      setSelectedCustomEmails([]);
    }

    if (filterMode === 'folder' && mode !== 'folder') {
      setSelectedFolder(null, null);
    }

    setFilterMode(mode);
    setSelectedFilterMode(mode);
  };

  const handleSelectFolder = (folderId: string, folderName: string) => {
    setSelectedFolder(folderId, folderName);
    handleOpenChange(false);
  };

  // Computes the next selection from the current snapshot instead of inside a
  // state updater: the updater must stay pure (StrictMode replays it), and the
  // store writes below are side effects.
  const toggleEmail = (id: string) => {
    const isCurrentlySelected = selectedEmails.has(id);

    const next = new Set(selectedEmails);
    if (!isCurrentlySelected && filterMode !== 'custom') {
      contextEmailIds?.forEach((contextId) => next.add(contextId));
    }
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }

    const newMode = next.size > 0 ? 'custom' : initialFilterMode;

    setSelectedEmails(next);
    setFilterMode(newMode);
    setSelectedFilterMode(newMode);
    setSelectedCustomEmails(newMode === 'custom' ? [...next] : []);
  };

  return {
    canUseCurrentEmail,
    canUseCurrentThread,
    composeMode,
    counterHighlight,
    debouncedSearch,
    ...emailData,
    filterMode,
    handleFilterModeChange,
    handleOpenChange,
    handleSelectFolder,
    ragSearchEnabled,
    resultCount,
    searchQuery,
    selectedCount,
    selectedEmails,
    selectedEmailsSectionRef,
    selectedFolderId,
    setSearchQuery,
    showCheckboxes,
    showSyncingState,
    toggleEmail,
  };
}
